import React, { useRef, useEffect, useState, useCallback } from 'react';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 400;
const PADDLE_WIDTH = 12;
const PADDLE_HEIGHT = 80;
const BALL_RADIUS = 10;
const WINNING_SCORE = 7;

let gameLoop = null;

export default function Pong() {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('START');
  const [playerScore, setPlayerScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [winner, setWinner] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  
  const gameRef = useRef({
    playerY: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2,
    aiY: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2,
    ball: { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, vx: 0, vy: 0, speed: 5 },
    ballTrail: [],
    particles: [],
    aiSpeed: 3.5,
    keys: { up: false, down: false },
  });

  const createParticle = useCallback((x, y, color) => {
    const particles = [];
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12;
      particles.push({
        x, y,
        vx: Math.cos(angle) * (3 + Math.random() * 3),
        vy: Math.sin(angle) * (3 + Math.random() * 3),
        life: 1,
        color,
      });
    }
    return particles;
  }, []);

  const resetBall = useCallback((scorer) => {
    const game = gameRef.current;
    const direction = scorer === 'player' ? -1 : 1;
    game.ball = {
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT / 2,
      vx: direction * 5,
      vy: (Math.random() - 0.5) * 4,
      speed: 5,
    };
    game.ballTrail = [];
  }, []);

  const resetGame = useCallback(() => {
    const game = gameRef.current;
    game.playerY = CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2;
    game.aiY = CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2;
    game.ball = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, vx: 0, vy: 0, speed: 5 };
    game.ballTrail = [];
    game.particles = [];
    game.aiSpeed = 3.5;
    setPlayerScore(0);
    setAiScore(0);
    setWinner(null);
  }, []);

  const drawGame = useCallback((ctx) => {
    const game = gameRef.current;
    
    ctx.fillStyle = '#0a0e27';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    ctx.strokeStyle = 'rgba(208,0,255,0.4)';
    ctx.setLineDash([15, 15]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH / 2, 0);
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#00d9ff';
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 10;
    ctx.fillRect(20, game.playerY, PADDLE_WIDTH, PADDLE_HEIGHT);
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#ff006e';
    ctx.shadowColor = '#ff006e';
    ctx.shadowBlur = 10;
    ctx.fillRect(CANVAS_WIDTH - 20 - PADDLE_WIDTH, game.aiY, PADDLE_WIDTH, PADDLE_HEIGHT);
    ctx.shadowBlur = 0;

    game.ballTrail.forEach((pos, i) => {
      const alpha = (i + 1) / game.ballTrail.length * 0.5;
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, BALL_RADIUS * (i + 1) / game.ballTrail.length, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#ffffff';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(game.ball.x, game.ball.y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    game.particles = game.particles.filter(p => {
      ctx.fillStyle = p.color.replace('1)', `${p.life})`);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4 * p.life, 0, Math.PI * 2);
      ctx.fill();
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.025;
      return p.life > 0;
    });
  }, []);

  const updateGame = useCallback(() => {
    const game = gameRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (game.keys.up) {
      game.playerY = Math.max(0, game.playerY - 8);
    }
    if (game.keys.down) {
      game.playerY = Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, game.playerY + 8);
    }

    const aiCenter = game.aiY + PADDLE_HEIGHT / 2;
    if (aiCenter < game.ball.y - 10) {
      game.aiY += game.aiSpeed;
    } else if (aiCenter > game.ball.y + 10) {
      game.aiY -= game.aiSpeed;
    }
    game.aiY = Math.max(0, Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, game.aiY));

    game.ball.x += game.ball.vx;
    game.ball.y += game.ball.vy;

    game.ballTrail.push({ x: game.ball.x, y: game.ball.y });
    if (game.ballTrail.length > 5) game.ballTrail.shift();

    if (game.ball.y - BALL_RADIUS <= 0 || game.ball.y + BALL_RADIUS >= CANVAS_HEIGHT) {
      game.ball.vy = -game.ball.vy;
    }

    const playerPaddleX = 20 + PADDLE_WIDTH;
    const aiPaddleX = CANVAS_WIDTH - 20 - PADDLE_WIDTH;

    if (game.ball.x - BALL_RADIUS <= playerPaddleX && 
        game.ball.y >= game.playerY && 
        game.ball.y <= game.playerY + PADDLE_HEIGHT) {
      game.ball.vx = Math.abs(game.ball.vx) * 1.05;
      game.ball.vy += (game.ball.y - (game.playerY + PADDLE_HEIGHT / 2)) * 0.1;
      game.ball.x = playerPaddleX + BALL_RADIUS;
    }

    if (game.ball.x + BALL_RADIUS >= aiPaddleX && 
        game.ball.y >= game.aiY && 
        game.ball.y <= game.aiY + PADDLE_HEIGHT) {
      game.ball.vx = -Math.abs(game.ball.vx) * 1.05;
      game.ball.vy += (game.ball.y - (game.aiY + PADDLE_HEIGHT / 2)) * 0.1;
      game.ball.x = aiPaddleX - BALL_RADIUS;
    }

    if (game.ball.x < 0) {
      setAiScore(s => {
        const newScore = s + 1;
        if (newScore >= WINNING_SCORE) {
          setWinner('AI');
          setGameState('WIN');
        }
        return newScore;
      });
      game.particles = [...game.particles, ...createParticle(game.ball.x, game.ball.y, 'rgba(255,0,110,1)')];
      resetBall('ai');
    }

    if (game.ball.x > CANVAS_WIDTH) {
      setPlayerScore(s => {
        const newScore = s + 1;
        if (newScore >= WINNING_SCORE) {
          setWinner('Player');
          setGameState('WIN');
        }
        return newScore;
      });
      game.particles = [...game.particles, ...createParticle(game.ball.x, game.ball.y, 'rgba(0,217,255,1)')];
      resetBall('player');
    }
  }, [createParticle, resetBall]);

  useEffect(() => {
    if (gameState === 'PLAYING') {
      if (!gameLoop) {
        resetBall('player');
        gameLoop = setInterval(() => {
          updateGame();
          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            drawGame(ctx);
          }
        }, 1000 / 60);
      }
    } else {
      if (gameLoop) {
        clearInterval(gameLoop);
        gameLoop = null;
      }
      if (gameState === 'START' || gameState === 'PAUSED') {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#0a0e27';
          ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          
          ctx.strokeStyle = 'rgba(208,0,255,0.4)';
          ctx.setLineDash([15, 15]);
          ctx.beginPath();
          ctx.moveTo(CANVAS_WIDTH / 2, 0);
          ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }

    return () => {
      if (gameLoop) {
        clearInterval(gameLoop);
        gameLoop = null;
      }
    };
  }, [gameState, updateGame, drawGame, resetBall]);

  useEffect(() => {
    let isTouching = false;
    let isMouseActive = false;

    const updateTouchPosition = (clientY) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scale = CANVAS_HEIGHT / rect.height;
      const targetY = (clientY - rect.top) * scale - PADDLE_HEIGHT / 2;
      gameRef.current.playerY = Math.max(0, Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, targetY));
    };

    const handleMouseMove = (e) => {
      if (!isMouseActive || gameState !== 'PLAYING') return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scale = CANVAS_HEIGHT / rect.height;
      const targetY = (e.clientY - rect.top) * scale - PADDLE_HEIGHT / 2;
      gameRef.current.playerY = Math.max(0, Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, targetY));
    };

    const handleMouseDown = () => {
      isMouseActive = true;
      if (gameState === 'START') {
        setGameState('PLAYING');
      }
    };

    const handleMouseUp = () => {
      isMouseActive = false;
    };

    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      
      if (key === ' ') {
        e.preventDefault();
        if (gameState === 'START') {
          setGameState('PLAYING');
        } else if (gameState === 'PLAYING') {
          setGameState('PAUSED');
        } else if (gameState === 'PAUSED') {
          setGameState('PLAYING');
        }
        return;
      }

      if (gameState === 'WIN') {
        if (key === 'enter' || key === ' ') {
          setGameState('START');
        }
        return;
      }

      const game = gameRef.current;
      if ((key === 'arrowup' || key === 'w') && !game.keys.up) {
        game.keys.up = true;
        isMouseActive = false;
      }
      if ((key === 'arrowdown' || key === 's') && !game.keys.down) {
        game.keys.down = true;
        isMouseActive = false;
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      const game = gameRef.current;
      if (key === 'arrowup' || key === 'w') {
        game.keys.up = false;
      }
      if (key === 'arrowdown' || key === 's') {
        game.keys.down = false;
      }
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      isTouching = true;
      if (gameState === 'START') {
        setGameState('PLAYING');
      }
      updateTouchPosition(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (isTouching && gameState === 'PLAYING') {
        updateTouchPosition(e.touches[0].clientY);
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      isTouching = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    const canvas = canvasRef.current;
    canvas?.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas?.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas?.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas?.addEventListener('mousemove', handleMouseMove);
    canvas?.addEventListener('mousedown', handleMouseDown);
    canvas?.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas?.removeEventListener('touchstart', handleTouchStart);
      canvas?.removeEventListener('touchmove', handleTouchMove);
      canvas?.removeEventListener('touchend', handleTouchEnd);
      canvas?.removeEventListener('mousemove', handleMouseMove);
      canvas?.removeEventListener('mousedown', handleMouseDown);
      canvas?.removeEventListener('mouseup', handleMouseUp);
    };
  }, [gameState]);

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: '700px', margin: '0 auto', width: '100%', padding: '0.5rem' }}>
      <div 
        className="mb-3 flex gap-16"
        style={{ fontFamily: 'monospace', fontSize: 'clamp(28px, 8vw, 48px)', fontWeight: 'bold' }}
      >
        <span style={{ color: '#00d9ff', textShadow: '0 0 20px #00d9ff' }}>{playerScore}</span>
        <span style={{ color: '#ff006e', textShadow: '0 0 20px #ff006e' }}>{aiScore}</span>
      </div>

      <div style={{ width: '100%', maxWidth: isMobile ? '88vw' : '700px', aspectRatio: isMobile ? '3/2' : `${CANVAS_WIDTH}/${CANVAS_HEIGHT}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="relative" style={{ width: '100%', aspectRatio: `${CANVAS_WIDTH}/${CANVAS_HEIGHT}` }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="rounded-lg"
            tabIndex={-1}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              border: '2px solid rgba(0,217,255,0.3)',
              boxShadow: '0 0 20px rgba(0,217,255,0.2)',
              cursor: 'pointer',
              touchAction: 'none',
              outline: 'none',
            }}
          />
          
          {gameState === 'START' && (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
              style={{ 
                background: 'rgba(10,14,39,0.9)',
                border: '1px solid #00d9ff',
                boxShadow: '0 0 30px rgba(0,217,255,0.3)',
                touchAction: 'none',
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                setGameState('PLAYING');
              }}
              onClick={() => setGameState('PLAYING')}
            >
              <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(16px, 5vw, 24px)', textShadow: '0 0 15px #00d9ff' }}>
                Tap or Press SPACE to Start
              </p>
              <p style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 'clamp(11px, 3vw, 14px)', marginTop: '10px' }}>
                First to 7 wins!
              </p>
            </div>
          )}

          {gameState === 'PAUSED' && (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
              style={{ 
                background: 'rgba(10,14,39,0.9)',
                border: '1px solid #d000ff',
                boxShadow: '0 0 30px rgba(208,0,255,0.3)',
                touchAction: 'none',
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                setGameState('PLAYING');
              }}
              onClick={() => setGameState('PLAYING')}
            >
              <p style={{ color: '#d000ff', fontFamily: 'monospace', fontSize: 'clamp(20px, 6vw, 28px)', textShadow: '0 0 15px #d000ff' }}>
                PAUSED
              </p>
              <p style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 'clamp(12px, 3.5vw, 16px)', marginTop: '10px' }}>
                Tap or Press SPACE to resume
              </p>
            </div>
          )}

          {gameState === 'WIN' && (
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
              style={{ 
                background: 'rgba(10,14,39,0.9)',
                border: `1px solid ${winner === 'Player' ? '#00d9ff' : '#ff006e'}`,
                boxShadow: `0 0 30px ${winner === 'Player' ? 'rgba(0,217,255,0.3)' : 'rgba(255,0,110,0.3)'}`,
                touchAction: 'none',
              }}
            >
              <p style={{ 
                color: winner === 'Player' ? '#00d9ff' : '#ff006e', 
                fontFamily: 'monospace', 
                fontSize: 'clamp(24px, 7vw, 36px)', 
                fontWeight: 'bold',
                textShadow: `0 0 20px ${winner === 'Player' ? '#00d9ff' : '#ff006e'}`,
                marginBottom: '10px'
              }}>
                {winner === 'Player' ? 'YOU WIN!' : 'AI WINS!'}
              </p>
              <p style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 'clamp(14px, 4vw, 16px)', marginBottom: '20px' }}>
                {playerScore} - {aiScore}
              </p>
              <button
                onClick={() => {
                  resetGame();
                  setGameState('START');
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  resetGame();
                  setGameState('START');
                }}
                style={{
                  background: 'transparent',
                  border: `2px solid ${winner === 'Player' ? '#00d9ff' : '#ff006e'}`,
                  color: winner === 'Player' ? '#00d9ff' : '#ff006e',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  cursor: 'pointer',
                  boxShadow: `0 0 15px ${winner === 'Player' ? 'rgba(0,217,255,0.4)' : 'rgba(255,0,110,0.4)'}`,
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = winner === 'Player' ? 'rgba(0,217,255,0.1)' : 'rgba(255,0,110,0.1)';
                  e.target.style.boxShadow = `0 0 25px ${winner === 'Player' ? 'rgba(0,217,255,0.6)' : 'rgba(255,0,110,0.6)'}`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.boxShadow = `0 0 15px ${winner === 'Player' ? 'rgba(0,217,255,0.4)' : 'rgba(255,0,110,0.4)'}`;
                }}
              >
                Play Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}